import React from "react";
import { useTranslation } from "react-i18next";
import { useFormat } from "hooks/useFormat";
import { Box, useTheme } from "@mui/material";
import Typography from "components/basic/typography";

import TicketStatusBadge from "../../../TicketStatusBadge";
import { CATEGORY_LABELS } from "../../../../constants";
import { requesterLabel } from "../../../../helpers";
import { SupportTicket } from "../../../../types";

interface TicketRowProps {
  ticket: SupportTicket;
  showRequester: boolean;
  onClick: () => void;
}

const TicketRow: React.FC<TicketRowProps> = ({
  ticket,
  showRequester,
  onClick,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("support");
  const format = useFormat();
  const unread = ticket.unreadCount > 0;

  return (
    <Box
      onClick={onClick}
      sx={{
        cursor: "pointer",
        px: 2,
        py: 1.5,
        borderBottom: `1px solid ${theme.palette.divider}`,
        "&:hover": { backgroundColor: theme.palette.action.hover },
      }}
    >
      <Box display="flex" alignItems="center" gap={1} minWidth={0}>
        {unread && (
          <Box
            aria-label={t("detail.unread")}
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              flexShrink: 0,
              backgroundColor: theme.palette.primary.main,
            }}
          />
        )}
        <Typography
          variant="h5"
          noWrap
          style={{ fontWeight: unread ? 700 : 500, flexGrow: 1 }}
          sx={{ minWidth: 0 }}
        >
          #{ticket.id} · {ticket.subject}
        </Typography>
        <TicketStatusBadge status={ticket.status} />
      </Box>

      <Box
        display="flex"
        flexWrap="wrap"
        alignItems="center"
        columnGap={1.5}
        mt={0.5}
      >
        <Typography
          variant="caption"
          style={{ color: theme.palette.text.secondary }}
        >
          {t(CATEGORY_LABELS[ticket.category])}
        </Typography>
        {showRequester && (
          <Typography
            variant="caption"
            style={{ color: theme.palette.text.secondary }}
          >
            {requesterLabel(ticket)}
          </Typography>
        )}
        <Typography
          variant="caption"
          style={{ color: theme.palette.text.secondary, marginLeft: "auto" }}
        >
          {format.timestamp(ticket.lastActivityAt)}
        </Typography>
      </Box>

      {ticket.lastCommentBody && (
        <Typography
          variant="body2"
          style={{
            marginTop: 4,
            color: theme.palette.text.secondary,
            display: "-webkit-box",
            WebkitLineClamp: 1,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {ticket.lastCommentBody}
        </Typography>
      )}
    </Box>
  );
};

export default TicketRow;
